import React, { useState, useContext, useMemo, useRef, useEffect } from 'react';
import loadable from '@loadable/component';
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';
import isEqual from 'lodash/isEqual';
import {
  array,
  arrayOf,
  bool,
  func,
  shape,
  string,
  oneOf,
  object,
  number,
} from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  propTypes,
} from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  parse,
  stringify,
  isSafeInternalPath,
} from '../../util/urlHelpers';
import {
  calculateDistance,
  ensureListing,
  ensureOwnListing,
  ensureUser,
  sanitizeHtml,
  stripHtmlTags,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  isBookingProcess,
  isBookingProcessAlias,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';
import MobileBottomNavbar from '../../components/MobileBottomNavbar/MobileBottomNavbar';

import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  manageDisableScrolling,
  isScrollingDisabled,
} from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import { withViewport } from '../../util/uiHelpers';

import {
  H4,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  Button,
  Modal,
  Form,
  FieldTextInput,
  AboutOwner,
  IconCollection,
  OrderPanelSkeleton,
  BookingFormErrorBoundary,
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import SystemErrorSection from '../../components/SystemErrorSection/SystemErrorSection';
import DescriptionRentalTermsTabs from '../ListingWizard/ListingWizardPreview/DescriptionRentalTermsTabs.js';
import InstructionsToUse from './InstructionToUse/InstructionToUse.js';
import SectionMapMaybe from './SectionMapMaybe.js';
import { getStoreTimeZone } from '../StoreHoursPage/StoreHoursForm/storeHoursUtils';
import AuthorListings from './AuthorListings/AuthorListings.js';
import SuggestedUses from '../../components/SuggestedUses/SuggestedUses.js';
import WhereWorksBest from '../../components/WhereWorksBest/WhereWorksBest.js';
import ReviewSummary from '../../components/ReviewSummary/ReviewSummary.js';
import StoreReviews from '../../components/StoreReviews/StoreReviews.js';
import EnvironmentalImpact from '../../components/EnvironmentalImpact/EnvironmentalImpact.js';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
  replicateListingThunk,
  showListing,
  fetchAuthorMoreListings,
  fetchMonthlyTimeSlots,
} from './ListingPage.duck';
import { isUsersEmailVerified } from '../TransactionPage/TransactionPage.duck';
import { fetchExchangeRates } from '../../ducks/exchangeRates.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
  getSuggestedUses,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import { addToCart, updateCart, updateCartAndReportResult, updateCartItemsBatch, removeFromCart, getExistingCartItem, groupCartItemsByAuthorAndDateRange } from '../../ducks/cart.duck';
import {
  selectCartDrawerGroup,
  getCartAuthorStoreName,
  getMostRecentCartItem,
  shouldPromptForDifferentOwner,
} from '../../util/cartHelpers';
import { fetchTransactionLineItems as fetchCartTransactionLineItems } from '../CartPage/CartPage.duck';
import { startCheckoutFromItems } from '../CheckoutPage/checkoutFromCart';
// Lazy-loaded (not statically imported) via @loadable/component - same pattern
// OrderPanel.js already uses for its own conditionally-rendered form variants
// (BookingDatesForm/BookingTimeForm/etc.), and required here specifically because
// @loadable/component (not React.lazy) supports this app's SSR chunk extraction via
// ChunkExtractor in server/index.js. Both panels are only ever needed once a user
// actually interacts with the cart - shipping their full bundle (react-select,
// react-final-form usage, the whole fee-calculation code path, EstimatedBreakDown)
// unconditionally into every listing page load was real, avoidable weight on a page
// most visitors never add anything to a cart from.
const CartDrawer = loadable(() =>
  import(/* webpackChunkName: "CartDrawer" */ '../../components/CartDrawer/CartDrawer')
);
const AllCartItemsPanel = loadable(() =>
  import(/* webpackChunkName: "AllCartItemsPanel" */ '../../components/CartDrawer/AllCartItemsPanel')
);
// RAS-2622: same lazy treatment - only ever needed when a Rent Now would start a
// second store's cart, which most listing visits never do.
const DifferentOwnerCartModal = loadable(() =>
  import(
    /* webpackChunkName: "DifferentOwnerCartModal" */ './DifferentOwnerCartModal/DifferentOwnerCartModal'
  )
);
import {
  DIFFERENT_OWNER_ACTION_START_NEW_CART,
  DIFFERENT_OWNER_ACTION_SELECT_MORE,
} from './DifferentOwnerCartModal/DifferentOwnerCartModal';
import classNames from 'classnames';
import { createResourceLocatorString } from '../../util/routes';

import css from './ListingPage.module.css';
import {
  composeValidators,
  required,
} from '../../util/validators.js';
import { isPermitted } from '../../util/currentUser.js';
import {
  buildProductSchema,
  buildBreadcrumbSchema,
  buildFaqSchema,
  getSchemaAvailability,
  generateStoreSlug,
  buildStoreCanonicalUrl,
  generateCategorySlug,
  generateCitySlug,
} from '../../util/seo';
import { USAGE_MULTIPLE } from '../../util/constants.js';
import { saveRentMoreUrl, getCartAuthorsAddedThisSession } from '../../util/sessionStorageManager.js';
import { getSearchWithStoreCart, STORE_CART_QUERY_PARAM, STORE_CART_OPEN_VALUE, DATE_RANGE_PARAMS } from '../../util/dateRangeHelpers';
import { getMarketplaceLabelsByOptionFromConfig } from '../../util/marketplaceCategoryLabels';
import Container from '../../components/Layout/Container.js';
import Grid from '../../components/Layout/Grid.js';
import Column from '../../components/Layout/Column.js';
import BookingSupport from '../../components/BookingSupport/BookingSupport.js';
import BlogListing from '../../components/BlogListing/BlogListing.js';
import ListingTopbar from './ListingTopbar.js';
import SectionGallery from './SectionGallery.js';
import ListingPageHeader from './ListingPageHeader.js';
import { getListingUnitCount } from '../../util/listingInventory';
import ListingActionBar from './ListingActionBar.js';
import InquiryForm from './InquiryForm/InquiryForm.js';
// import CopyListingModal from './CopyListingModal.js';
import { LanguageContext } from '../../context/LanguageContext';
import { getGalleryHeroPreload } from './listingGalleryImages';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const getUrlWithStoreCartOpen = url => {
  // `url` comes from the `searchUrl` query param (RentalCard's outgoing link), which is
  // attacker-controllable via a crafted URL. Reject anything that isn't a same-origin
  // relative path so history.push can't be handed an absolute/protocol-relative URL -
  // that would throw a cross-origin pushState SecurityError and crash the Rent Now flow.
  if (!isSafeInternalPath(url)) return null;
  // /rental-owner/668d1bce-18a1-4ca3-a226-c07791b602a9?city=Sas%20Nagar&endDate=2026-03-11&name=rahul%20ram&startDate=2026-03-09
  const [pathWithSearch, hashFragment] = url.split('#');
  const questionMarkIndex = pathWithSearch.indexOf('?');
  const pathname =
    questionMarkIndex >= 0
      ? pathWithSearch.slice(0, questionMarkIndex)
      : pathWithSearch;
  const search =
    questionMarkIndex >= 0 ? pathWithSearch.slice(questionMarkIndex) : '';
  const updatedSearch = getSearchWithStoreCart(search, true);
  const hash = hashFragment ? `#${hashFragment}` : '';

  return `${pathname}${updatedSearch}${hash}`;
};

const { UUID, Money } = sdkTypes;

const CopyListingModal = props => {
  const [showFields, setShowFields] = useState(false);

  const {
    show,
    onClose,
    onManageDisableScrolling,
    currentListingTitle,
    listingId,
    currentListingDescription,
    intl,
    onReplicateListing,
    history,
    routes,
    disableCopyListing,
    replicateListingError,
    replicateListingInProgress,
  } = props;

  const formIntials = {
    title: currentListingTitle,
    description: currentListingDescription,
  };
  const replicateListingErrorMsg = intl.formatMessage({
    id: 'ListingPage.CopyListingModal.replicateListingError',
  });

  // title validators
  const maxLengthMessage = intl.formatMessage(
    { id: 'ListingPage.CopyListingModal.maxLength' },
    {
      maxLength: TITLE_MAX_LENGTH,
    }
  );
  const titleRequiredMessage = intl.formatMessage({
    id: 'ListingPage.CopyListingModal.titleRequired',
  });
  const mustChangeTitleMessage = intl.formatMessage(
    { id: 'ListingPage.CopyListingModal.mustChangeTitle' },
    { words: 'one' }
  );

  const maxLength60Message = maxLength(maxLengthMessage, TITLE_MAX_LENGTH);
  const titleValueValidator = stringValidator({
    message: mustChangeTitleMessage,
    currentStr: currentListingTitle,
    shouldDifferentBy: 1,
  });
  const titleValidators = composeValidators(
    required(titleRequiredMessage),
    maxLength60Message,
    titleValueValidator
  );

  // Description validators
  const descriptionRequiredMsg = intl.formatMessage({
    id: 'ListingPage.ReplicateModal.descriptionRequired',
  });
  const mustChangeDescriptionMsg = intl.formatMessage(
    { id: 'ListingPage.CopyListingModal.mustChangeDescription' },
    { words: 'ten' }
  );
  const descriptionRequired = required(descriptionRequiredMsg);
  const descriptionValueValidator = stringValidator({
    message: mustChangeDescriptionMsg,
    currentStr: currentListingDescription,
    shouldDifferentBy: 10,
  });

  const descriptionValidators = composeValidators(
    descriptionRequired,
    descriptionValueValidator
  );

  const handleReplicateListing = values => {
    const { title, description } = values;
    onReplicateListing({ listingId, title, description }).then(res => {
      const { listingId, type } = res;
      history.push(
        createResourceLocatorString('AIUpdateListingPage', routes, {
          slug: 'draft',
          id: listingId,
          type,
          tab: 'details',
        })
      );
    });
  };

  if (disableCopyListing) {
    return null;
  }

  return (
    <Modal
      className={css.copyListingModal}
      isOpen={show}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
    >
      {showFields ? (
        <FinalForm
          initialValues={formIntials}
          onSubmit={handleReplicateListing}
          render={formRenderProps => {
            const {
              handleSubmit,
              formId,
              invalid,
              submitInProgress,
            } = formRenderProps;

            const submitDisabled = invalid || submitInProgress;

            return (
              <Form className={css.replicateModalFrom} onSubmit={handleSubmit}>
                <span className={css.whyChange}>
                  <FormattedMessage id="ListingPage.CopyListingModal.whyChange" />
                </span>
                {replicateListingError ? (
                  <span className={css.replicateError}>
                    {replicateListingErrorMsg}
                  </span>
                ) : null}
                <FieldTextInput
                  id={`${formId}title`}
                  name="title"
                  className={css.title}
                  type="text"
                  label={intl.formatMessage({
                    id: 'ListingPage.ReplicateModal.title',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'ListingPage.ReplicateModal.titlePlaceholder',
                  })}
                  maxLength={TITLE_MAX_LENGTH}
                  validate={titleValidators}
                />

                <FieldTextInput
                  id={`${formId}description`}
                  name="description"
                  className={css.description}
                  type="textarea"
                  label={intl.formatMessage({
                    id: 'ListingPage.ReplicateModal.description',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'ListingPage.ReplicateModal.descriptionPlaceholder',
                  })}
                  validate={descriptionValidators}
                />
                <Button
                  className={css.submitButton}
                  type="submit"
                  inProgress={replicateListingInProgress}
                  disabled={submitDisabled}
                >
                  <FormattedMessage id="ListingPage.CopyListingModal.replicateButton" />
                </Button>
              </Form>
            );
          }}
        />
      ) : (
        <div>
          <span>
            <FormattedMessage id="ListingPage.replicateListing" />
          </span>
          <div className={css.actionBtns}>
            <Button
              className={css.replicateBtn}
              onClick={() => setShowFields(true)}
            >
              <FormattedMessage id="ListingPage.replicateButton" />
            </Button>
            <SecondaryButton onClick={onClose}>
              <FormattedMessage id="ListingPage.replicateLaterButton" />
            </SecondaryButton>
          </div>
        </div>
      )}
    </Modal>
  );
};

[
  {
    key: 'checkoutOptions',
    scope: 'public',
    schemaType: 'multi-enum',
    includeForListingTypes: ['general'],
    enumOptions: [
      {
        label: 'Charged once per cart only',
        value: 'Instruction to',
        disabled: false,
      },
      {
        label: 'Charged once per listing',
        value: 'oncePerListing',
        disabled: false,
      },
      { label: 'Charged once per item', value: 'oncePerItem', disabled: false },
    ],
  },
];

const getLongestRentalPeriod = (prices, intl) => {
  if (!prices || typeof prices !== 'object') {
    throw new Error(
      intl.formatMessage({ id: 'ListingPage.error.missingOrInvalidPrices' })
    );
  }

  // Priority order for the longest rental period
  const rentalPeriods = [
    'twentyEightDaysPrice',
    'oneWeekPrice',
    'fiveDaysPrice',
    'fourDaysPrice',
    'threeDaysPrice',
    'twoDaysPrice',
    'perDayPrice',
    'overnightPrice',
    'perHourPrice',
    'perMinutePrice',
  ];

  // Find the longest rental period available in the prices object
  let longestPeriodKey = null;
  for (const period of rentalPeriods) {
    if (prices[period]) {
      longestPeriodKey = period;
      break;
    }
  }

  if (!longestPeriodKey) {
    throw new Error(
      intl.formatMessage({ id: 'ListingPage.error.noValidRentalPeriods' })
    );
  }

  return longestPeriodKey;
};

const getDefaultDailyPrice = (listing, intl, defaultPriceKey) => {
  if (!listing || !listing.attributes || !listing.attributes.publicData) {
    throw new Error(
      intl.formatMessage({ id: 'ListingPage.error.invalidListingData' })
    );
  }

  const publicData = listing.attributes.publicData;
  const prices = publicData.prices;

  if (!prices || typeof prices !== 'object') {
    throw new Error(
      intl.formatMessage({ id: 'ListingPage.error.missingOrInvalidPrices' })
    );
  }

  // Find the longest rental period available in the prices object
  let longestPeriodKey = defaultPriceKey || getLongestRentalPeriod(prices, intl);

  if (!longestPeriodKey) {
    throw new Error(
      intl.formatMessage({ id: 'ListingPage.error.noValidRentalPeriods' })
    );
  }

  const longestPeriodPrice = prices[longestPeriodKey];

  if (
    !longestPeriodPrice ||
    !longestPeriodPrice.amount ||
    !longestPeriodPrice.currency
  ) {
    throw new Error(
      intl.formatMessage({ id: 'ListingPage.error.invalidPriceData' })
    );
  }

  let dailyPrice;

  // Calculate the daily price based on the rental period
  switch (longestPeriodKey) {
    case 'twentyEightDaysPrice':
      dailyPrice = longestPeriodPrice.amount / 28;
      break;
    case 'oneWeekPrice':
      dailyPrice = longestPeriodPrice.amount / 7;
      break;
    case 'fiveDaysPrice':
      dailyPrice = longestPeriodPrice.amount / 5;
      break;
    case 'fourDaysPrice':
      dailyPrice = longestPeriodPrice.amount / 4;
      break;
    case 'threeDaysPrice':
      dailyPrice = longestPeriodPrice.amount / 3;
      break;
    case 'twoDaysPrice':
      dailyPrice = longestPeriodPrice.amount / 2;
      break;
    case 'overnightPrice':
      dailyPrice = longestPeriodPrice.amount;
      break;
    case 'perDayPrice':
      dailyPrice = longestPeriodPrice.amount;
      break;
    case 'perHourPrice':
      dailyPrice = longestPeriodPrice.amount;
      break;
    case 'perMinutePrice':
      dailyPrice = longestPeriodPrice.amount * 60;
      break;
    default:
      throw new Error(
        intl.formatMessage({ id: 'ListingPage.error.unhandledRentalPeriod' })
      );
  }

  return new Money(dailyPrice, longestPeriodPrice.currency);
};

const getDefaultUnit = (listing, intl = null) => {
  const pricingKeys = listing?.attributes?.publicData?.prices || {};

  // Find the longest rental period available in the prices object
  let longestPeriodKey = getLongestRentalPeriod(pricingKeys, intl);

  // Define mappings for daily and hourly units
  const dailyKeys = [
    'perDayPrice',
    'twoDaysPrice',
    'threeDaysPrice',
    'fourDaysPrice',
    'fiveDaysPrice',
    'oneWeekPrice',
    'twentyEightDaysPrice',
    'overnightPrice',
  ];
  const hourlyKeys = ['perHourPrice', 'perMinutePrice'];

  // Helper function to determine the unit
  const determineUnit = key => {
    if (dailyKeys.includes(key)) {
      return 'day';
    } else if (hourlyKeys.includes(key)) {
      return 'hour';
    }
    return null;
  };

  if (longestPeriodKey && pricingKeys[longestPeriodKey]) {
    return determineUnit(longestPeriodKey);
  }

  const availableKeys = Object.keys(pricingKeys);
  if (availableKeys.length > 0) {
    return determineUnit(availableKeys[0]);
  }

  return null;
};

export const applyListingLanguage = (listing, locale) => {
  if (
    listing &&
    listing.attributes &&
    listing.attributes.publicData &&
    listing.attributes.publicData[locale]
  ) {
    const defaultLang = listing.attributes.publicData.defaultLanguage || 'en';

    // ✅ If the listing is already in the requested locale, no translation needed
    if (locale === defaultLang) {
      return listing;
    }

    const langData = listing.attributes.publicData[locale];
    const updatedAttributes = { ...listing.attributes };
    const updatedPublicData = { ...updatedAttributes.publicData };

    // Helper to translate array fields
    const translateArray = (field, itemKeyPrefix) => {
      if (!Array.isArray(updatedPublicData[field]))
        return updatedPublicData[field];
      const arr = updatedPublicData[field];
      const translated = arr.map((item, idx) => {
        const key = `${itemKeyPrefix}_${idx}`;
        return langData[key] !== undefined ? langData[key] : item;
      });
      return translated;
    };

    // Helper to translate faqs array
    const translateFaqs = () => {
      if (!Array.isArray(updatedPublicData.faqs)) return updatedPublicData.faqs;
      return updatedPublicData.faqs.map((faq, idx) => {
        const qKey = `faqs_${idx}_question`;
        const aKey = `faqs_${idx}_answer`;
        return {
          question:
            langData[qKey] !== undefined ? langData[qKey] : faq.question,
          answer: langData[aKey] !== undefined ? langData[aKey] : faq.answer,
        };
      });
    };

    // Helper to translate instructions (object or string)
    const translateInstructions = () => {
      const translated = langData.instructions;
      if (!translated) return updatedPublicData.instructions;
      if (
        typeof updatedPublicData.instructions === 'object' &&
        typeof translated === 'object'
      ) {
        return { ...updatedPublicData.instructions, ...translated };
      }
      return translated;
    };

    // Helper to translate online_review_summary (object or string)
    const translateOnlineReviewSummary = () => {
      const translated = langData.online_review_summary;
      if (!translated) return updatedPublicData.online_review_summary;
      if (
        typeof updatedPublicData.online_review_summary === 'object' &&
        typeof translated === 'object'
      ) {
        return { ...updatedPublicData.online_review_summary, ...translated };
      }
      return translated;
    };

    // Helper to translate additional_charges (array of objects)
    const translateAdditionalCharges = () => {
      if (!Array.isArray(updatedPublicData.additional_charges))
        return updatedPublicData.additional_charges;
      return updatedPublicData.additional_charges.map((charge, idx) => {
        const descKey = `additional_charges_${idx}_description`;
        return {
          ...charge,
          description:
            langData[descKey] !== undefined
              ? langData[descKey]
              : charge.description,
        };
      });
    };

    // Helper to translate environmental_impact (object with benefits array and paragraph string)
    const translateEnvironmentalImpact = () => {
      const originalImpact = updatedPublicData.environmental_impact || {};
      const translatedBenefits = Array.isArray(originalImpact.benefits)
        ? originalImpact.benefits.map((benefit, idx) => {
            const key = `environmental_impact_benefits_${idx}`;
            return langData[key] !== undefined ? langData[key] : benefit;
          })
        : [];

      const paragraphKey = 'environmental_impact_paragraph';
      const translatedParagraph =
        langData[paragraphKey] !== undefined
          ? langData[paragraphKey]
          : originalImpact.paragraph;

      return {
        ...originalImpact,
        benefits: translatedBenefits,
        paragraph: translatedParagraph,
      };
    };

    // List of simple fields to translate
    const simpleFields = [
      'title',
      'description',
      'feeDescription',
      'where_the_item_can_be_used',
    ];
    simpleFields.forEach(field => {
      if (langData[field] !== undefined) {
        updatedAttributes[field] = langData[field];
        if (updatedPublicData[field] !== undefined) {
          updatedPublicData[field] = langData[field];
        }
      }
    });

    // Translate arrays
    updatedPublicData.alternative_search_titles = translateArray(
      'alternative_search_titles',
      'alternative_search_titles'
    );
    updatedPublicData.environmental_impact = translateArray(
      'environmental_impact',
      'environmental_impact'
    );
    updatedPublicData.suggested_usage_of_this_item = translateArray(
      'suggested_usage_of_this_item',
      'suggested_usage_of_this_item'
    );
    updatedPublicData.return_care_instructions = translateArray(
      'return_care_instructions',
      'return_care_instructions'
    );
    updatedPublicData.faqs = translateFaqs();
    updatedPublicData.instructions = translateInstructions();
    updatedPublicData.online_review_summary = translateOnlineReviewSummary();
    updatedPublicData.additional_charges = translateAdditionalCharges();
    updatedPublicData.environmental_impact = translateEnvironmentalImpact();

    // Translate feeDescription if present in langData
    if (langData.feeDescription !== undefined) {
      updatedPublicData.feeDescription = langData.feeDescription;
    }

    // Translate any other direct publicData fields if present in langData
    Object.keys(langData).forEach(key => {
      if (
        !simpleFields.includes(key) &&
        !key.match(
          /^(alternative_search_titles|environmental_impact|suggested_usage_of_this_item|return_care_instructions|faqs|additional_charges)_\d+/
        ) &&
        key !== 'instructions' &&
        key !== 'online_review_summary'
      ) {
        if (Object.prototype.hasOwnProperty.call(updatedPublicData, key)) {
          updatedPublicData[key] = langData[key];
        }
      }
    });

    updatedAttributes.publicData = updatedPublicData;
    return {
      ...listing,
      attributes: updatedAttributes,
    };
  }
  return listing;
};

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );

  // RAS-2500: slide-out cart drawer, opened in place of navigating to CartPage
  // whenever Rent Now succeeds.
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);
  // RAS-2501: "view all cart items" panel, opened via the drawer's own back-link -
  // the two panels form a navigation stack on the same surface (only one visible at a
  // time), not independent overlays, so they share this single pair of booleans.
  const [isAllCartItemsPanelOpen, setIsAllCartItemsPanelOpen] = useState(false);
  // Set when the drawer is opened by clicking a specific row in AllCartItemsPanel, so
  // it can be scoped to that exact (authorId, date range) group instead of the default
  // "current listing's author, most recently added item's date range" behavior used by
  // Rent Now. Cleared whenever the drawer is opened via Rent Now instead, so a stale
  // override from a previous panel visit can never leak into a fresh Rent Now.
  const [cartDrawerOverride, setCartDrawerOverride] = useState(null);
  // RAS-2622: the Rent Now that is waiting on the "different owner" confirmation.
  // Holds the add-to-cart params captured at the moment Rent Now was pressed (so a
  // later form edit can't change what the confirmed add writes) plus the cart item the
  // headline names. null = popup closed. `differentOwnerAction` is which button is
  // mid-flight, so the popup can lock itself against a second tap.
  const [differentOwnerPrompt, setDifferentOwnerPrompt] = useState(null);
  const [differentOwnerAction, setDifferentOwnerAction] = useState(null);
  // One-way latch (same pattern as BookingDatesForm.js's hasAppliedOnceRef): CartDrawer
  // and AllCartItemsPanel are now lazy-loaded via loadable() below, but merely being
  // present in this component's JSX (even with isOpen=false) is enough to trigger their
  // chunk fetch, since loadable() doesn't know about an isOpen prop - it only reacts to
  // being mounted. Gating their JSX inclusion on this ref means the chunk is only
  // requested the first time the user actually triggers Rent Now or the cart UI,
  // instead of on every listing page load regardless of whether the cart is ever
  // touched. Once true it stays true, so both panels keep the "always mounted, CSS-
  // toggled" behavior their own state persistence (formEpoch, etc.) depends on for the
  // rest of this page's lifetime.
  const hasOpenedCartUIRef = useRef(false);
  if (isCartDrawerOpen || isAllCartItemsPanelOpen || differentOwnerPrompt) {
    hasOpenedCartUIRef.current = true;
  }

  const { locale } = useContext(LanguageContext);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews,
    reviewsByUsers,
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    monthlyTimeSlots,
    onFetchTimeSlots,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    selectedCurrency,
    exchangeRates,
    fetchRatesError,
    fetchRatesInProgress,
    authorListings: authorListingsRaw,
    authorListingsInProgress,
    authorListingsError,
    onAddToCart,
    addToCartInProgress,
    addToCartError,
    cartNotification,
    cartItemsByAuthor,
    cartPageLineItems,
    cartPageFetchLineItemsInProgress,
    cartPageFetchLineItemsError,
    onFetchCartLineItems,
    onUpdateCartItem,
    onUpdateCartItemWithResult,
    onUpdateCartItemsBatch,
    onRemoveCartItem,
    dispatch,
    onReplicateListing,
    replicateListingError,
    replicateListingInProgress,
    isVerified,
    totalListings,
    blogs,
    fetchBlogsInProgress,
    fetchBlogsError,
    viewport,
    currentUserLocationInfo,
    detectedCurrentLocation,
    usersEmailVerified,
    onShowListing,
    onFetchExchangeRates,
    onIsUsersEmailVerified,
    onFetchAuthorMoreListings,
    onFetchMonthlyTimeSlots,
  } = props;

  const authorListings =
    Array.isArray(authorListingsRaw) &&
    authorListingsRaw.map(authorListing => {
      return applyListingLanguage(authorListing, locale);
    });

  const { allowCopy, fixExpiredCartItemId } = parse(location.search);
  const [isCopyListingPopup, setIsCopyListingPopup] = useState(allowCopy);

  // RAS: "Update Dates" button on an expired CartPage item links here with
  // ?fixExpiredCartItemId=<id> instead of the pre-existing ?cartItemId= prefill param,
  // so it can't affect that separate prefill flow. addToCart always writes a NEW cart
  // entry when the picked dates differ from what's already in the cart (see
  // addToCartHandler below), so the stale expired entry is removed explicitly here
  // once the fresh booking has actually landed - never before, so an abandoned visit
  // (browser back, no submit) leaves the expired item untouched.
  const clearFixExpiredCartItemIdFromUrl = () => {
    const updatedParams = parse(location.search);
    delete updatedParams.fixExpiredCartItemId;
    const newSearchString = `?${stringify(updatedParams)}`;
    history.replace(`${location.pathname}${newSearchString}`, location.state);
  };
  const removeFixedExpiredCartItem = writtenItemId => {
    if (!fixExpiredCartItemId) return;
    if (fixExpiredCartItemId !== writtenItemId) {
      onRemoveCartItem(fixExpiredCartItemId);
    }
    clearFixExpiredCartItemIdFromUrl();
  };

  // Extract the search part of the URL
  const searchParams = new URLSearchParams(location.search);

  // Get the value of the 'search' parameter (which is another encoded query string) - legacy support
  const nestedQueryString = searchParams.get('search');
  const decodedNestedQueryString = decodeURIComponent(nestedQueryString || '');
  const nestedParams = new URLSearchParams(decodedNestedQueryString);

  // Get the value of the 'queryID' parameter (check both direct and nested)
  const queryID = searchParams.get('queryID') || nestedParams.get('queryID');

  // Get the 'from' parameter to determine where user came from
  // Check both direct params and nested params (legacy)
  const fromParam = searchParams.get('from') || nestedParams.get('from');
  const isStorefrontFlow = fromParam === 'storefront';

  // Get the search URL to preserve user's search state for "Rent More Items"
  // Check direct param first, then nested (legacy)
  const searchUrlParam =
    searchParams.get('searchUrl') || nestedParams.get('searchUrl');
  const decodedSearchUrl = searchUrlParam || null;
  const bookNowParam =
    searchParams.get('bookNow') || nestedParams.get('bookNow');
  const shouldAutoFocusBooking =
    typeof bookNowParam === 'string' &&
    ['true', '1', 'yes'].includes(bookNowParam.toLowerCase());

  const rawSlug = rawParams?.slug;
  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isPendingApprovalVariant =
    rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListingRaw =
    isPendingApprovalVariant || isDraftVariant
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const currentListing = useMemo(() => {
    return applyListingLanguage(currentListingRaw || {}, locale);
  }, [currentListingRaw, locale]);

  // Load more content
  // Render full listing content in SSR/initial HTML for SEO.
  // Progressive lazy rendering can hide critical content from crawlers.
  const [shouldRenderRestOfPage, setShouldRenderRestOfPage] = useState(true);
  const triggerRef = useRef(null);
  useEffect(() => {
    if (shouldRenderRestOfPage) return;

    const target = triggerRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          console.log('Trigger is intersecting!');
          setShouldRenderRestOfPage(true);
          observer.disconnect(); // observe only once
        }
      },
      {
        root: null, // viewport
        // Require a bit more intersection and shrink the bottom of the root
        // so that on small viewports (mobile) we don't trigger immediately at load.
        threshold: 0.25,
        rootMargin: '0px 0px -20% 0px',
      }
    );

    observer.observe(target);
    return () => {
      observer.disconnect();
    };
  }, [currentListing?.id, shouldRenderRestOfPage]);

  // The desktop sidebar used to be positioned by JS here: a MutationObserver-driven
  // effect wrote an inline `top: min(90, viewportHeight - columnHeight)px !important` on
  // a position:sticky column. That value goes NEGATIVE as soon as the column is taller
  // than the viewport (it almost always is - booking panel + map + secure booking), and a
  // negative sticky top anchors the column by its BOTTOM, so the booking/date card - the
  // thing the page exists for - scrolled up out of view and stayed there.
  //
  // The sidebar is now plain static (.desktopColumn in ListingPage.module.css): it
  // scrolls with the page in lockstep with the left column, which is both what makes the
  // map and the rest of the right-hand content reachable and what keeps the date
  // selection sitting at the top of the column where it's visible on load. No sticky
  // offsets to compute, so no JS and no MutationObserver re-layout loop on every panel
  // state change.

  // Scroll to top when navigating to a new listing
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentListing?.id]);

  // Client-side dispatches for follow-up data that does not feed Product JSON-LD
  // (and therefore does not need to be in the SSR critical path):
  //   - exchange rates (price-conversion UI only)
  //   - author email-verified badge (UI only)
  //   - monthly time slots (booking calendar UI; booking processes only)
  //   - author's other listings (UI rail; not in any listing-page JSON-LD)
  // SSR still ships reviews and blogs because they feed aggregateRating and
  // subjectOf in the Product schema.
  const currentListingUuid = currentListing?.id?.uuid;

  // RAS-2500: cart drawer data - scoped to this listing's author, kept in its own
  // state.CartPage slice (NOT state.ListingPage.lineItems, which OrderPanel's own
  // booking-form pricing already owns on this same page - reusing that slot would
  // have the drawer's multi-item cart pricing and the booking panel's single-item
  // pricing stomp on each other).
  //
  // Declared here (before this component's conditional early returns further below,
  // e.g. shouldShowPublicListingPage / showListingError / needsRedirect) rather than
  // next to handleCartDrawerCheckout where they're used - a hook declared after an
  // early return only fires on renders that don't take that return, which violates
  // the Rules of Hooks ("Rendered more hooks than during the previous render").
  // RAS-2501: when a row is picked in AllCartItemsPanel, cartDrawerOverride carries
  // the specific author to scope to instead of the current listing's own author -
  // falls back to the pre-existing behavior (this listing's author) when no override
  // is active, which covers every Rent Now flow unchanged.
  const cartDrawerAuthorId = cartDrawerOverride?.authorId || currentListing?.author?.id?.uuid;
  const cartDrawerAllAuthorItems = (cartDrawerAuthorId && cartItemsByAuthor?.[cartDrawerAuthorId]) || [];
  // Scope to items sharing one date range - either the override's exact range (RAS-2501
  // row click) or, absent an override, the most recently added/updated item's range for
  // this author (cart.duck.js's sortItemsByAuthorRecency keeps index 0 most-recent) -
  // matches StoreProfilePage's CartModal, which restricts its cart view to one date
  // range at a time (there driven by a page-level date filter; ListingPage has no such
  // filter, so the reference date range is whichever item's Rent Now most recently
  // opened this drawer).
  //
  // Memoized on content plus the override, not just the redux reference: the `|| []`
  // fallback above creates a fresh empty-array literal on every render whenever this
  // author has no items yet, and .filter() always returns a new array regardless of
  // whether the underlying data changed. Without this, CartDrawer's items prop gets a
  // new reference on every unrelated parent re-render, which tears down and rebuilds
  // its debounced-fetch effect before the initial 100ms fetch timer ever fires -
  // confirmed by instrumenting CartDrawer's fetchLineItems directly, not guessed.
  //
  // The dependency is a cheap, hand-built key touching only the small fields that
  // could actually change (id, quantity, dates, fee selections) - not
  // JSON.stringify(cartDrawerAllAuthorItems), which used to serialize each item's full
  // denormalized `listing` (images array, author profile, publicData, etc.) on every
  // render just to detect a change. For a cart with several items that's real,
  // avoidable work happening synchronously in the render path.
  const cartDrawerItemsKey = cartDrawerAllAuthorItems
    .map(item =>
      [
        item.id,
        item.addedAt,
        item.quantity,
        // Not part of the grouping key (see below), but a change to it still changes
        // what the row renders, so it has to invalidate this memo.
        item.package,
        item.bookingDates?.startDate,
        item.bookingDates?.endDate,
        (item.selectedFeeTypes || []).join(','),
        JSON.stringify(item.usageMultipleValues || {}),
        item.pickupAtOwnerLocation,
        item.returnAtOwnerLocation,
      ].join('|')
    )
    .join(';');
  //
  // RAS-2520 follow-up: this now delegates to the SAME grouping function AllCartItemsPanel
  // uses (groupCartItemsByAuthorAndDateRange, cart.duck.js) instead of reimplementing a
  // filter beside it. A previous pass scoped the drawer by package category and, for
  // hourly bookings, at minute precision - finer than the duck's (author, day, day) key.
  // The two then disagreed about what a "group" is: the all-items panel counted a 1-hour
  // and a 5-minute booking on the same day as one 2-item group, while the drawer showed
  // only the 5-minute one and silently dropped the other. Deriving both from one function
  // makes that class of divergence impossible rather than merely fixed.
  //
  // Note this deliberately does NOT separate categories. Two bookings of the same listing
  // in different categories are separate cart entries (see isSameCartBooking in
  // cart.duck.js, which does keep them apart) - but they are still rented from the same
  // store over the same dates and checked out together, so they belong in one drawer,
  // each row carrying its own price.
  const cartDrawerItems = useMemo(() => {
    const groups = groupCartItemsByAuthorAndDateRange(
      cartDrawerAuthorId ? { [cartDrawerAuthorId]: cartDrawerAllAuthorItems } : {}
    );
    if (!groups.length) {
      return cartDrawerAllAuthorItems;
    }
    const group = selectCartDrawerGroup(groups, cartDrawerOverride);
    return group ? group.items : [];
  }, [cartDrawerItemsKey, cartDrawerOverride]);
  // Does the cart hold anything beyond the single group the drawer is currently scoped
  // to? Derived from the SAME grouping function the drawer's own scoping and
  // AllCartItemsPanel use, over the whole cart rather than one author, so the count can
  // never disagree with what that panel would actually list. Drives whether the drawer
  // renders its "All orders" link at all.
  const hasOtherCartGroups = useMemo(
    () => groupCartItemsByAuthorAndDateRange(cartItemsByAuthor || {}).length > 1,
    [cartItemsByAuthor]
  );

  const cartDrawerLineItems = cartDrawerAuthorId ? cartPageLineItems?.[cartDrawerAuthorId] : null;
  const cartDrawerFetchLineItemsInProgress = !!(
    cartDrawerAuthorId && cartPageFetchLineItemsInProgress?.[cartDrawerAuthorId]
  );
  const cartDrawerFetchLineItemsError = cartDrawerAuthorId
    ? cartPageFetchLineItemsError?.[cartDrawerAuthorId]
    : null;

  // CartDrawer now owns its own line-items fetching entirely (initial fetch on open,
  // debounced re-fetch on form changes to delivery/pickup/add-ons, matching CartModal's
  // own self-contained pattern) via the onFetchCartLineItems prop passed to it below -
  // no separate effect needed here.

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.add('hide-crisp-mobile');
    return () => {
      document.body.classList.remove('hide-crisp-mobile');
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !currentListingUuid) return;

    const authorId = currentListing?.author?.id?.uuid;
    const transactionProcessAlias =
      currentListing?.attributes?.publicData?.transactionProcessAlias || '';

    if (!exchangeRates || exchangeRates.length === 0) {
      onFetchExchangeRates();
    }
    if (authorId) {
      onIsUsersEmailVerified(null, { uuid: authorId });
      onFetchAuthorMoreListings(authorId, currentListingUuid);
    }
    if (isBookingProcessAlias(transactionProcessAlias)) {
      onFetchMonthlyTimeSlots(currentListing);
    }
  }, [currentListingUuid]);

  const listingSlug =
    rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id &&
    currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage =
    pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return (
      <NamedRedirect
        name="ListingPage"
        params={params}
        search={location.search}
      />
    );
  }

  if (!rawSlug && listingSlug && listingSlug !== 'no-slug') {
    return (
      <NamedRedirect
        name="ListingPage"
        params={params}
        search={location.search}
      />
    );
  }

  const topbar = (
    <>
      <div className={css.topbarMobile}>
        <ListingTopbar
          history={history}
          currentListing={currentListingRaw}
          routeConfiguration={routeConfiguration}
        />
      </div>
      <div className={css.topbarDesktop}>
        <TopbarContainer currentListing={currentListingRaw} />
      </div>
    </>
  );

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage />;
  } else if (showListingError) {
    // Genuine system error (e.g. transient 5xx) fetching a listing that does
    // exist - this is not a "gone" listing, so it must not redirect and must
    // not look like everything worked. Show a clear system-error state
    // instead of the old ad hoc "Try again" block.
    return (
      <Page title={intl.formatMessage({ id: 'ListingPage.errorLoadingListingTitle' })} scrollingDisabled={scrollingDisabled}>
        {topbar}
        <SystemErrorSection
          contextLabel={intl.formatMessage({ id: 'ListingPage.errorLoadingListingContext' })}
          onRetry={() => onShowListing(listingId, config)}
        />
      </Page>
    );
  } else if (
    !currentListing.id ||
    !currentListing?.attributes?.publicData?.prices
  ) {
    // Still loading the listing
    return (
      <LoadingPage
        topbar={topbar}
        scrollingDisabled={scrollingDisabled}
        intl={intl}
      />
    );
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const {
    city,
    postCode,
    region,
    country,
    instructions,
    quantity,
    online_review_summary,
    environmental_impact,
    faqs,
    enabledOptionalFields = [],
    defaultLanguage,
    defaultPriceKey,
  } = publicData || {};

  const { suggestedUses, showSuggestedUses } = getSuggestedUses(publicData);

  // Category breadcrumb: resolve human-readable label for the listing's category
  const categoryOption = publicData?.category || null;
  const categoryLabels = categoryOption
    ? getMarketplaceLabelsByOptionFromConfig(categoryOption)
    : {};
  const categoryLabel = categoryLabels[locale] || categoryLabels['en'] || null;
  const categorySearchUrl = categoryOption
    ? `${config.marketplaceRootURL}/search?category=${categoryOption}`
    : null;

  // Redirect /rentals/:slug/:id → /rentals/:city/:slug/:id when city is available.
  // Also corrects /rentals/:wrong-city/:slug/:id to the authoritative city slug.
  // Never redirects draft or variant pages — those use separate URL patterns.
  const urlCitySlug = rawParams.city || null;
  const urlCategorySlug = rawParams.category || null;
  const listingCitySlugForRedirect = generateCitySlug(city) || null;
  const listingCategorySlugForRedirect = listingCitySlugForRedirect
    ? generateCategorySlug(categoryOption)
    : null;
  const isEditableVariant = isDraftVariant || isPendingApprovalVariant;

  // Determine canonical route name for the NamedRedirect target
  const canonicalRouteName = (() => {
    if (listingCitySlugForRedirect && listingCategorySlugForRedirect) return 'ListingPageCityCategory';
    if (listingCitySlugForRedirect) return 'ListingPageCity';
    return 'ListingPage';
  })();

  // Redirect when any URL segment is wrong or missing compared to canonical
  const needsRedirect =
    urlCitySlug !== listingCitySlugForRedirect ||
    urlCategorySlug !== listingCategorySlugForRedirect;

  if (needsRedirect && !isEditableVariant && currentListing.id) {
    return (
      <NamedRedirect
        name={canonicalRouteName}
        params={{
          ...(listingCitySlugForRedirect ? { city: listingCitySlugForRedirect } : {}),
          ...(listingCategorySlugForRedirect ? { category: listingCategorySlugForRedirect } : {}),
          slug: listingSlug,
          id: listingId.uuid,
        }}
        search={location.search}
      />
    );
  }

  const showInstructions =
    !!instructions && enabledOptionalFields.includes('instructions');
  const showOnlineReviewSummary =
    !!online_review_summary &&
    enabledOptionalFields.includes('online_review_summary');
  const showFaqs = !!faqs && enabledOptionalFields.includes('faqs');

  const listingDistance = calculateDistance(
    currentUserLocationInfo?.origin?.lat,
    currentUserLocationInfo?.origin?.lng,
    geolocation?.lat,
    geolocation?.lng
  );
  const showInMiles = currentUserLocationInfo?.country === 'US';
  const defaultPrice = getDefaultDailyPrice(currentListing, intl, defaultPriceKey);
  const defaultUnit = getDefaultUnit(currentListing, intl);
  const richedTitle = richText(title, {
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
    longWordClass: css.longWord,
  });

  const richTitle = (
    <span>
      {!!city ? (
        <FormattedMessage
          id="ListingPage.titleWithLocation"
          values={{ title: richedTitle, city }}
        />
      ) : (
        richedTitle
      )}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable &&
    currentListing.author.id.uuid === currentUser.id.uuid;

  const transactionProcessAlias = publicData?.transactionProcessAlias;
  const processName = resolveLatestProcessName(
    transactionProcessAlias?.split('/')?.[0]
  );
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking
    ? 'booking'
    : isPurchase
    ? 'purchase'
    : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  // Owners opt into publishing their exact address from Store Settings. Absent or
  // non-true means the listing map keeps the privacy radius circle (the default).
  const showAddressInMarketplace =
    ensuredAuthor?.attributes?.profile?.publicData?.showAddressInMarketplace === true;
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing &&
    (processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected);
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage
        id="ListingPage.payoutDetailsWarning"
        values={{ processType }}
      />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');
  const { storeName, rating = 5, storeAddress, origin: authorOrigin, city: storeCity, storeHours } =
    ensuredAuthor.attributes.profile?.publicData || {};
  const authorStoreCitySlug = generateCitySlug(storeAddress?.city || storeCity);
  // Store hours are wall-clock times at the store's own location, not the viewer's or
  // server's — resolve the store's IANA timezone so open/closed status is computed there.
  const storeTimeZone = getStoreTimeZone(storeAddress?.origin || authorOrigin);

    // Display name for store breadcrumb — storeName preferred over displayName
  const storeBreadcrumbLabel = storeName || authorDisplayName || null;

  // RAS-2501: when the drawer is scoped to a cartDrawerOverride (opened via a row in
  // AllCartItemsPanel), its author/storeName need to reflect THAT group's own store,
  // not this listing page's author - derived from the group's own items, which already
  // carry a denormalized listing.author (cart.duck.js's getItemsByAuthor includes it).
  // Falls back to the existing ensuredAuthor/storeName when no override is active, so
  // every Rent Now flow is unaffected.
  const cartDrawerAuthorEntity = cartDrawerOverride
    ? cartDrawerAllAuthorItems[0]?.listing?.author || ensuredAuthor
    : ensuredAuthor;
  const cartDrawerStoreName = cartDrawerOverride
    ? cartDrawerAuthorEntity?.attributes?.profile?.publicData?.storeName ||
      userDisplayNameAsString(cartDrawerAuthorEntity, '')
    : storeName || authorDisplayName;

  // Store URL for storefront breadcrumb — only derived when user came from store
  const storeBreadcrumbUrl = isStorefrontFlow
    ? buildStoreCanonicalUrl({
        rootUrl: config.marketplaceRootURL,
        storeName: storeName || authorDisplayName,
        city: storeAddress?.city || storeCity || null,
        userId: ensuredAuthor.id?.uuid,
        activeTab: 'home',
      })
    : null;

  const { formattedPrice: defaultFormattedPrice } = priceData(
    defaultPrice,
    defaultPrice.currency,
    intl
  );
  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
    queryID,
  });

  const usageMultipleCharges =
    publicData?.additionalFees?.filter(
      fee => fee.feeCalculationType === USAGE_MULTIPLE
    ) || [];

  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
    lineItems,
    usageMultipleCharges,
    locale,
    queryID,
  });

  const isCurrentlyClosed =
    currentListing.attributes.state === LISTING_STATE_CLOSED;

  const handleOrderSubmit = values => {
    if (isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };
  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');

  // Derive hero alt text from seoTags if available, fall back to listing title
  const heroListingImage = publicData?.listingImages?.[0];
  const heroAltText = heroListingImage?.seoTags?.altText || title;

  // LCP hint for the gallery's first slide. Derived from currentListing.images[0] (the slide the
  // gallery actually renders first) rather than publicData.listingImages[0] — the two arrays are
  // ordered independently, so the old hint could describe a different photo entirely on top of
  // asking for a different variant. Handed to <Page> so it is emitted inside <head>.
  //
  // Deliberately NOT useMemo: this line sits below several conditional early returns
  // (LoadingPage / NotFoundPage / error states around lines 1172-1297), so a hook here runs on
  // some renders and not others — React then throws "rendered more hooks than during the
  // previous render" (#310) and the whole listing page blanks out. The computation is a small
  // map build plus string concatenation, so calling it directly costs nothing worth a hook.
  const heroImagePreload = getGalleryHeroPreload(currentListing);

  // Extend facebookImages with alt on the first image so og:image:alt is emitted
  // openGraphMetaProps() reads i.alt and outputs og:image:alt when present (seo.js P1-T8)
  const facebookImagesWithAlt = facebookImages.map((img, i) =>
    i === 0 ? { ...img, alt: heroAltText } : img
  );
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = !!city
    ? intl.formatMessage(
        { id: 'ListingPage.schemaTitlePrimary' },
        {
          title,
          city,
          price: defaultFormattedPrice,
          unit: defaultUnit,
          marketplaceName,
        }
      )
    : intl.formatMessage(
        { id: 'ListingPage.schemaTitleSecondary' },
        {
          title,
          price: defaultFormattedPrice,
          unit: defaultUnit,
          marketplaceName,
        }
      );

  const plainDescription = stripHtmlTags(description);
  const truncatedDescription = plainDescription.length > 110
    ? plainDescription.slice(0, 110).replace(/\s+\S*$/, '') + '…'
    : plainDescription;
  const seoOptimisedDescription = (!!city
    ? intl.formatMessage(
        { id: 'ListingPage.seoOptimisedDescriptionPrimary' },
        {
          title,
          city,
          price: defaultFormattedPrice,
          unit: defaultUnit,
          description: truncatedDescription, // ← truncated version stays here
        }
      )
    : intl.formatMessage(
        { id: 'ListingPage.seoOptimisedDescriptionSecondary' },
        {
          title,
          price: defaultFormattedPrice,
          unit: defaultUnit,
          description: truncatedDescription, // ← truncated version stays here
        }
      )
  ).slice(0, 160).trim();

  // Then separately, pass schemaDescription into your schema builder

  // Canonical listing URL — includes city slug when available for SEO
  const listingCitySlug = generateCitySlug(city) || null;
  const listingCategoryCanonicalSlug = listingCitySlug
    ? generateCategorySlug(categoryOption)
    : null;
  const canonicalListingUrl = (() => {
    if (intl.locale !== 'en') {
      // Non-English pages self-reference per Google's hreflang spec.
      // location.pathname already has the correct localized path, city, category,
      // localized slug, and UUID — redirect has corrected city/category before this runs.
      const cleanPath = location.pathname.replace(/\/(draft|edit|pending-approval)\/?$/, '');
      return `${config.marketplaceRootURL}${cleanPath}`;
    }
    const base = `${config.marketplaceRootURL}/rentals`;
    if (listingCitySlug && listingCategoryCanonicalSlug) {
      return `${base}/${listingCitySlug}/${listingCategoryCanonicalSlug}/${listingSlug}/${listingId.uuid}`;
    }
    if (listingCitySlug) {
      return `${base}/${listingCitySlug}/${listingSlug}/${listingId.uuid}`;
    }
    return `${base}/${listingSlug}/${listingId.uuid}`;
  })();

  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const addToCartHandler = params => {
    if (isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      // If this exact listing + date range is already in the cart, re-adding via
      // addToCart would just merge quantities (cart.duck.js's addToCart thunk adds the
      // new quantity on top of the existing one - see existingCartItemIndex/
      // getExistingCartItem there), which for a plain Rent Now click (not a deliberate
      // "add more") almost immediately trips the "Maximum quantity available" error for
      // single-quantity listings. So this never calls addToCart again for a match -
      // but the item's booking panel selections (quantity, fees/add-ons, fulfillment)
      // may have genuinely changed since it was added, and those changes need to land
      // somewhere. updateCart (dispatched as onUpdateCartItem, already used by the cart
      // drawer itself for saving per-item selections) replaces rather than sums these
      // fields on the matched item, so it doesn't reintroduce the summing bug above.
      // RAS-2520: package included so an hourly Rent Now on a listing that also has a
      // daily entry at the same instants isn't mistaken for a re-add of that entry (see
      // isSameCartBooking in cart.duck.js) - which would update the daily item and open
      // the drawer on it instead of adding the hourly booking the renter asked for.
      const alreadyInCart = getExistingCartItem(
        cartDrawerAllAuthorItems,
        params.listingId,
        params.bookingDates?.startDate,
        params.bookingDates?.endDate,
        params.selectedPackage
      );
      if (alreadyInCart) {
        const sortedFeeTypes = arr => [...(arr || [])].sort();
        const hasChanges =
          Number(params.quantity) !== Number(alreadyInCart.quantity) ||
          !isEqual(params.usageMultipleValues || {}, alreadyInCart.usageMultipleValues || {}) ||
          !isEqual(
            sortedFeeTypes(params.selectedFeeTypes),
            sortedFeeTypes(alreadyInCart.selectedFeeTypes)
          ) ||
          !!params.pickupAtOwnerLocation !== !!alreadyInCart.pickupAtOwnerLocation ||
          !!params.returnAtOwnerLocation !== !!alreadyInCart.returnAtOwnerLocation;

        const openDrawer = () => {
          // The renter picked dates that collided with an already-valid cart entry -
          // still clean up the stale expired item they were trying to fix.
          removeFixedExpiredCartItem(alreadyInCart.id);

          // Mirrors the success branch below exactly, since from the user's
          // perspective this IS a successful "add" - the item is already there.
          if (isStorefrontFlow && decodedSearchUrl) {
            const storefrontCartUrl = getUrlWithStoreCartOpen(decodedSearchUrl);
            if (storefrontCartUrl) {
              history.push(storefrontCartUrl);
              return;
            }
          }
          // RAS-2520: scope the drawer to THIS booking, rather than clearing the scope
          // and letting it fall back to the most recently added group. The renter pressed
          // Rent Now on a specific booking; when that booking already exists and is older
          // than something else in their cart, "most recent" is a different booking
          // entirely - which is how pressing Rent Now on an overnight booking opened the
          // drawer on a 2-day daily booking of the same listing for the same dates.
          setCartDrawerOverride({
            authorId: params.authorId,
            startDate: alreadyInCart.bookingDates?.startDate,
            endDate: alreadyInCart.bookingDates?.endDate,
          });
          setIsCartDrawerOpen(true);
        };

        if (hasChanges) {
          onUpdateCartItem({
            id: alreadyInCart.id,
            authorId: params.authorId,
            quantity: params.quantity,
            usageMultipleValues: params.usageMultipleValues,
            selectedFeeTypes: params.selectedFeeTypes,
            pickupAtOwnerLocation: params.pickupAtOwnerLocation,
            returnAtOwnerLocation: params.returnAtOwnerLocation,
          })
            .then(openDrawer)
            .catch(openDrawer); // Open regardless - the item is already in the cart either way.
        } else {
          openDrawer();
        }
        return;
      }

      // RAS-2622: adding this listing would start a brand-new cart for a store the
      // shopper has nothing from yet, next to a cart they started IN THIS SESSION with
      // another store (a cart left over from a previous visit doesn't count - client
      // clarification 2026-09-18; the per-session store list is written by
      // cart.duck.js's addToCart). Ask first - the add itself is deferred to the
      // popup's choice (see the handleDifferentOwner* handlers below), captured with
      // THESE params. Marketplace listing pages only: a listing reached from inside a
      // store (isStorefrontFlow) is a store-scoped experience, and the ticket excludes
      // store-only views.
      const sessionCartAuthorIds = isStorefrontFlow ? [] : getCartAuthorsAddedThisSession();
      if (
        !isStorefrontFlow &&
        shouldPromptForDifferentOwner(cartItemsByAuthor, params.authorId, sessionCartAuthorIds)
      ) {
        setDifferentOwnerPrompt({
          params,
          existingItem: getMostRecentCartItem(cartItemsByAuthor, sessionCartAuthorIds),
        });
        return;
      }

      performAddToCart(params);
    }
  };

  // The actual add + what happens after it succeeds. Split out of addToCartHandler so
  // the RAS-2622 popup's "Start a New Cart" runs exactly this path (same Rent More URL
  // bookkeeping, same expired-item cleanup, same drawer scoping) rather than a copy of
  // it. `onSuccess` overrides only the post-add step; the default is today's behavior.
  // Returns the add's promise (already settled-safe: failures are swallowed here, as
  // they always were - redux's cart error notification is what surfaces them).
  function performAddToCart(params, { onSuccess } = {}) {
    // Save URL for "Rent More Items" redirect (session-only)
    // Priority: referrer URL (search/store) > current listing page
    const urlToSave =
      decodedSearchUrl ||
      (typeof window !== 'undefined' ? window.location.pathname : null);
    if (params.authorId && urlToSave) {
      saveRentMoreUrl(params.authorId, urlToSave);
    }

    const openDrawerOnNewBooking = () => {
      if (isStorefrontFlow && decodedSearchUrl) {
        const storefrontCartUrl = getUrlWithStoreCartOpen(decodedSearchUrl);
        if (storefrontCartUrl) {
          history.push(storefrontCartUrl);
          return;
        }
      }

      // RAS-2500: Rent Now opens the slide-out cart drawer in place, instead
      // of navigating away to CartPage.
      //
      // RAS-2520: scoped to the booking just added rather than cleared. It happens to
      // be the most recent one right now, so the fallback would agree - but relying on
      // that couples the drawer's contents to add-order, which is what broke the
      // already-in-cart path above. Being explicit costs nothing and can't drift.
      setCartDrawerOverride({
        authorId: params.authorId,
        startDate: params.bookingDates?.startDate,
        endDate: params.bookingDates?.endDate,
      });
      setIsCartDrawerOpen(true);
    };

    return onAddToCart(config, params, queryID)
      .then(() => {
        // A brand-new cart entry was just created for the (necessarily different,
        // non-expired) dates the renter picked - always safe to drop the stale one.
        removeFixedExpiredCartItem();
        (onSuccess || openDrawerOnNewBooking)();
        return true;
      })
      .catch(() => {
        // do nothing redux will manage.
        return false;
      });
  }

  // RAS-2622: the popup's three outcomes. Dismissals add nothing. The two adding
  // choices lock the popup (differentOwnerAction) until the add settles, then close it
  // - on failure the shopper stays put and sees the cart error notification, and for
  // "Select More Items" is NOT navigated to a store whose cart didn't get created.
  const closeDifferentOwnerPrompt = () => {
    setDifferentOwnerPrompt(null);
    setDifferentOwnerAction(null);
  };

  const handleDifferentOwnerStartNewCart = () => {
    if (!differentOwnerPrompt || differentOwnerAction) return;
    setDifferentOwnerAction(DIFFERENT_OWNER_ACTION_START_NEW_CART);
    performAddToCart(differentOwnerPrompt.params).then(closeDifferentOwnerPrompt);
  };

  const handleDifferentOwnerSelectMoreItems = () => {
    if (!differentOwnerPrompt || differentOwnerAction) return;
    setDifferentOwnerAction(DIFFERENT_OWNER_ACTION_SELECT_MORE);
    const { params: promptParams } = differentOwnerPrompt;
    performAddToCart(promptParams, {
      onSuccess: () => {
        const storeUrl = getStoreUrlWithCartOpen(promptParams.bookingDates);
        if (storeUrl) {
          history.push(storeUrl);
        } else {
          // No author id to route to (shouldn't happen: the gate requires one) - fall
          // back to showing the new cart right here rather than doing nothing visible.
          setCartDrawerOverride({
            authorId: promptParams.authorId,
            startDate: promptParams.bookingDates?.startDate,
            endDate: promptParams.bookingDates?.endDate,
          });
          setIsCartDrawerOpen(true);
        }
      },
    }).then(closeDifferentOwnerPrompt);
  };

  // This listing's store page, opened straight onto the Store Cart panel. ?storeCart=open
  // is the store page's own "cart requested" flag (RAS-2583): on landing it scopes the
  // panel to the remembered rental range the booking form just saved, so the panel opens
  // on the booking that was just added and the cart badge shows its count - the ticket's
  // "visibly active, not silently created" requirement. The explicit calendar-day params
  // are the fallback for when localStorage is unavailable (private mode) and the
  // remembered range can't be read; they're days in the STORE's zone, which is how the
  // store page reads them back (same convention as getRentMoreItemsUrl).
  //
  // Built with createResourceLocatorString - not buildStoreCanonicalUrl's hardcoded
  // /rental-owner/ - so the path is right in every locale's translated routes.
  const getStoreUrlWithCartOpen = bookingDates => {
    const authorId = ensuredAuthor?.id?.uuid;
    if (!authorId) return null;
    const toStoreDay = d => (storeTimeZone ? moment(d).tz(storeTimeZone) : moment(d)).format('YYYY-MM-DD');
    const search = {
      [STORE_CART_QUERY_PARAM]: STORE_CART_OPEN_VALUE,
      ...(bookingDates?.startDate && bookingDates?.endDate
        ? {
            [DATE_RANGE_PARAMS.START_DATE]: toStoreDay(bookingDates.startDate),
            [DATE_RANGE_PARAMS.END_DATE]: toStoreDay(bookingDates.endDate),
          }
        : {}),
    };
    const slug = generateStoreSlug(storeName || authorDisplayName);
    return authorStoreCitySlug
      ? createResourceLocatorString(
          'ProfilePage',
          routeConfiguration,
          { city: authorStoreCitySlug, slug, id: authorId },
          search
        )
      : createResourceLocatorString('ProfilePageNoCity', routeConfiguration, { slug, id: authorId }, search);
  };

  // Copy inputs for the popup. The existing item's listing/author are the denormalized
  // entities cart.duck.js's getItemsByAuthor attaches - absent when that listing has
  // since been deleted, which the modal handles with shorter copy.
  const differentOwnerExistingItem = differentOwnerPrompt?.existingItem;
  const differentOwnerExistingItemTitle = differentOwnerExistingItem?.listing?.attributes?.title || '';
  const differentOwnerExistingItemImage = differentOwnerExistingItem?.listing?.images?.[0] || null;
  const differentOwnerExistingStoreName = getCartAuthorStoreName(
    differentOwnerExistingItem?.listing?.author
  );

  // RAS-2501: back-link inside CartDrawer opens this panel instead of navigating to
  // CartPage - the two panels form a single navigation stack on the same surface, so
  // only one is ever open at a time.
  //
  // Clearing cartDrawerOverride here matters: without it, a user who opened the
  // drawer scoped to a specific group (via handleSelectCartGroup below), then came
  // back to this panel, then returned to the booking panel and rented a genuinely
  // different, new date range, would still have the OLD group's override sitting
  // around. It happens to get overwritten correctly once a brand-new cart item is
  // actually created (addToCartHandler's success path clears it), but there's no
  // reason to let a stale override survive a screen transition where it's no longer
  // meaningful - the moment the drawer isn't showing that specific selection anymore,
  // the scoping that produced it shouldn't outlive it.
  const handleViewAllCartItems = () => {
    setIsCartDrawerOpen(false);
    setCartDrawerOverride(null);
    setIsAllCartItemsPanelOpen(true);
  };

  // RAS-2501: tapping a row in AllCartItemsPanel scopes the drawer to that exact
  // (authorId, date range) group and swaps back to it - see cartDrawerOverride above.
  const handleSelectCartGroup = group => {
    setCartDrawerOverride({
      authorId: group.authorId,
      startDate: group.startDate,
      endDate: group.endDate,
    });
    setIsAllCartItemsPanelOpen(false);
    setIsCartDrawerOpen(true);
  };

  const handleCartDrawerCheckout = (itemsWithFormValues = cartDrawerItems) => {
    startCheckoutFromItems({
      items: itemsWithFormValues,
      activeAuthorId: cartDrawerAuthorId,
      lineItems: { [cartDrawerAuthorId]: cartDrawerLineItems },
      fetchLineItemsInProgress: { [cartDrawerAuthorId]: cartDrawerFetchLineItemsInProgress },
      setItemsInProgress: false,
      removeFromCartInProgress: false,
      currentUser,
      routeConfiguration,
      dispatch,
      history,
    });
  };

  const isCurrentUser =
    currentListing.author?.id?.uuid === currentUser?.id?.uuid;
  const disableCopyListing = !isPermitted(currentUser) || isCurrentUser;

  const isMobile = viewport?.width < 1024;

  const showEnvironmentalImpactSection =
    environmental_impact &&
    publicData?.reason_for_listing?.includes('help_the_environment');
  const environmentalImpactData = Array.isArray(environmental_impact)
    ? { bullets: environmental_impact }
    : environmental_impact?.benefits && environmental_impact?.paragraph
    ? {
        bullets: environmental_impact.benefits,
        paragraph: environmental_impact.paragraph,
      }
    : null;

  const averageRating = reviews.length > 0
    ? reviews.reduce((sum, r) => sum + (r?.attributes?.rating || 0), 0) / reviews.length
    : null;

  // Uses loaded reviews length — may undercount if reviews are paginated.
  // TODO: wire to pagination meta when available to reflect true total.
  const reviewCount = reviews.length;

  // Build schema array with Product, BreadcrumbList, FAQ, and Reviews
  const productSchema = buildProductSchema({
    listing: currentListing,
    canonicalUrl: canonicalListingUrl,
    rootUrl: config.marketplaceRootURL,
    defaultPriceKey,
    displayPrice: defaultPrice,
    averageRating,
    reviewCount,
    blogs,
  });

  const breadcrumbSchema = buildBreadcrumbSchema([
    {
      name: intl.formatMessage({ id: 'Breadcrumb.home' }),
      url: config.marketplaceRootURL,
    },
    // Category middle item — only included when category label and URL are available
    ...(categoryLabel && categorySearchUrl
      ? [{ name: categoryLabel, url: categorySearchUrl }]
      : []),
    {
      name: title,
      url: canonicalListingUrl,
    },
  ].filter(item => item.name && item.url));

  // FAQ schema — only if listing-specific FAQ content exists in publicData
  const listingFaqs = faqs || null;
  const faqSchema = listingFaqs?.length > 0
    ? buildFaqSchema(listingFaqs)
    : null;

  // noindex draft, closed, pending listings, and listings with no valid slug
  const NOINDEX_STATES = ['draft', 'closed', 'pendingApproval'];
  const hasNoSlug = rawParams.slug === 'no-slug' || !rawParams.slug;
  const listingRobots = NOINDEX_STATES.includes(currentListing?.attributes?.state) || hasNoSlug
    ? 'noindex,follow'
    : 'index,follow';

  const reviewSchemas =
    Array.isArray(reviews) && reviews.length > 0
      ? reviews
          .slice(0, 5)
          .map(review => {
            const reviewAuthor = review?.author;
            const reviewRating = review?.attributes?.rating || 5;
            const reviewText = review?.attributes?.content || '';
            const reviewDate = review?.attributes?.createdAt || '';

            return {
              '@context': 'https://schema.org',
              '@type': 'Review',
              itemReviewed: {
                '@type': 'Product',
                '@id': `${canonicalListingUrl}#product`,
                name: title,
              },
              author: {
                '@type': 'Person',
                name:
                  reviewAuthor?.attributes?.profile?.displayName || 'Anonymous',
              },
              datePublished: reviewDate
                ? new Date(reviewDate).toISOString()
                : undefined,
              reviewBody: reviewText,
              reviewRating: {
                '@type': 'Rating',
                ratingValue: reviewRating,
                bestRating: 5,
                worstRating: 1,
              },
            };
          })
          .filter(review => review.reviewBody)
      : [];

  const allSchemas = [productSchema, breadcrumbSchema, faqSchema, ...reviewSchemas].filter(Boolean);

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={storeName || authorDisplayName}
      description={seoOptimisedDescription}
      facebookImages={facebookImagesWithAlt}
      twitterImages={twitterImages}
      currentListing={currentListingRaw}
      canonicalUrl={canonicalListingUrl}
      robots={listingRobots}
      schema={allSchemas}
      openGraphType="product"
      preloadImage={heroImagePreload}
    >
      <Modal
        id="ListingPage.inquiry"
        contentClassName={css.inquiryModalContent}
        // Manual QA: on mobile, Message Owner is reachable from inside the booking
        // modal, which sets its own z-index:999999 to sit above the sticky footer and
        // bottom nav. This modal was still on Modal.module.css's default
        // .isOpenInPortal (z-index:100), so opening it from there rendered it
        // underneath the booking modal - it looked like nothing happened. className is
        // additive on Modal's root (see Modal.js), so this only lifts this one modal,
        // one step above the booking modal, leaving every other modal untouched.
        // Closing it simply reveals the booking modal again underneath, unchanged.
        className={css.inquiryModalAboveBookingModal}
        isOpen={inquiryModalOpen}
        onClose={() => setInquiryModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <InquiryForm
          className={css.inquiryForm}
          submitButtonWrapperClassName={css.inquirySubmitButtonWrapper}
          listingTitle={title}
          authorDisplayName={authorDisplayName}
          sendInquiryError={sendInquiryError}
          onSubmit={onSubmitInquiry}
          inProgress={sendInquiryInProgress}
          isVerified={isVerified}
          rating={rating || 5}
        />
      </Modal>
      {hasOpenedCartUIRef.current && (
        <>
          <CartDrawer
            isOpen={isCartDrawerOpen}
            onClose={() => {
              setIsCartDrawerOpen(false);
              setCartDrawerOverride(null);
            }}
            onManageDisableScrolling={onManageDisableScrolling}
            items={cartDrawerItems}
            author={cartDrawerAuthorEntity}
            storeName={cartDrawerStoreName}
            lineItems={cartDrawerLineItems}
            fetchLineItemsInProgress={cartDrawerFetchLineItemsInProgress}
            fetchLineItemsError={cartDrawerFetchLineItemsError}
            exchangeRates={exchangeRates}
            exchangeRatesReady={!fetchRatesInProgress && exchangeRates.length > 0}
            currentUserCurrency={selectedCurrency}
            marketplaceName={config.marketplaceName}
            onQuantityChange={(id, quantity) => onUpdateCartItem({ id, quantity })}
            onRemove={onRemoveCartItem}
            onCheckout={handleCartDrawerCheckout}
            onFetchLineItems={onFetchCartLineItems}
            onSaveCartItemSelections={onUpdateCartItem}
            onSaveCartItemSelectionsBatch={onUpdateCartItemsBatch}
            onUpdateCartItemWithResult={onUpdateCartItemWithResult}
            authorId={cartDrawerAuthorId}
            onViewAllCartItems={handleViewAllCartItems}
            hasOtherCartGroups={hasOtherCartGroups}
            // RAS-2511/RAS-2512: after a successful date/time Apply, the group's items
            // now live under NEW dates - re-scope cartDrawerOverride to match, or
            // cartDrawerItems' own (authorId, old dates) filter would stop matching
            // them and the drawer would appear to go empty despite the write
            // succeeding.
            onDatesApplied={newBookingDates => {
              setCartDrawerOverride({
                authorId: cartDrawerAuthorId,
                startDate: newBookingDates.startDate,
                endDate: newBookingDates.endDate,
              });
            }}
            intl={intl}
          />
          <AllCartItemsPanel
            isOpen={isAllCartItemsPanelOpen}
            onClose={() => setIsAllCartItemsPanelOpen(false)}
            onManageDisableScrolling={onManageDisableScrolling}
            itemsByAuthor={cartItemsByAuthor}
            onSelectGroup={handleSelectCartGroup}
            intl={intl}
          />
          <DifferentOwnerCartModal
            isOpen={!!differentOwnerPrompt}
            existingItemTitle={differentOwnerExistingItemTitle}
            existingItemImage={differentOwnerExistingItemImage}
            existingStoreName={differentOwnerExistingStoreName}
            newItemTitle={title}
            newItemImage={currentListing?.images?.[0] || null}
            newStoreName={storeName || authorDisplayName}
            inProgress={differentOwnerAction}
            onClose={closeDifferentOwnerPrompt}
            onContinueBrowsing={closeDifferentOwnerPrompt}
            onStartNewCart={handleDifferentOwnerStartNewCart}
            onSelectMoreItems={handleDifferentOwnerSelectMoreItems}
            onManageDisableScrolling={onManageDisableScrolling}
          />
        </>
      )}
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={topbar}
        footer={<FooterContainer />}
        mobileBottomNavbar={<MobileBottomNavbar />}
      >
        <Container className={css.mainContainer}>
          <Grid>
            <Column>
              <Grid columns={12} className={css.grid}>
                <Column lg={8} md={8} sm={12} className={css.mainGrid}>
                  {/* <CopyListingModal
                    disableCopyListing={disableCopyListing}
                    show={isCopyListingPopup}
                    onClose={() => setIsCopyListingPopup(false)}
                    onManageDisableScrolling={onManageDisableScrolling}
                    onReplicateListing={onReplicateListing}
                    currentListingTitle={title}
                    currentListingDescription={description}
                    listingId={rawParams.id}
                    intl={intl}
                    history={history}
                    routes={routeConfiguration}
                    replicateListingInProgress={replicateListingInProgress}
                    replicateListingError={replicateListingError}
                  /> */}

                  {/* Breadcrumb Navigation (Optional but SEO-friendly) */}
                  <nav aria-label={intl.formatMessage({ id: 'ListingPageCarousel.breadcrumb' })} className={css.breadcrumb}>
                    {/* Item 1: Always — Marketplace home */}
                    <NamedLink
                      className={css.separator}
                      style={{ marginLeft: 0 }}
                      name="LandingPage"
                    >
                      <span>
                        {intl.formatMessage({ id: 'Breadcrumb.home' })}
                      </span>
                    </NamedLink>

                    {/* Item 2: Context-aware — Store or Category or nothing */}
                    {isStorefrontFlow && storeBreadcrumbUrl && storeBreadcrumbLabel ? (
                      <>
                        <IconCollection name="arrowSeparator" />
                        <a href={storeBreadcrumbUrl} className={css.separator}>
                          {storeBreadcrumbLabel}
                        </a>
                      </>
                    ) : !isStorefrontFlow && categoryLabel && categorySearchUrl ? (
                      <>
                        <IconCollection name="arrowSeparator" />
                        <a href={categorySearchUrl} className={css.separator}>
                          {categoryLabel}
                        </a>
                      </>
                    ) : null}

                    {/* Item 3: Always — Current listing (non-clickable) */}
                    <IconCollection name="arrowSeparator" />
                    <p aria-current="page" className={css.separator}>
                      {title}
                    </p>
                  </nav>

                  {/* Listing Page Header — single H1 instance for both desktop and mobile */}
                  <ListingPageHeader
                    className={css.pillsContainerUnified}
                    title={title}
                    city={city}
                    region={region}
                    country={country}
                    listingDistance={listingDistance}
                    showInMiles={showInMiles}
                    richTitle={richTitle}
                    rating={rating}
                    unitCount={getListingUnitCount(currentListing?.attributes?.publicData)}
                  />
                  {/* <div className={css.mainColumnForProductLayout}> */}
                  {currentListing.id && noPayoutDetailsSetWithOwnListing ? (
                    <ActionBarMaybe
                      className={css.actionBarForProductLayout}
                      isOwnListing={isOwnListing}
                      listing={currentListing}
                      showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                      locale={locale}
                    />
                  ) : null}
                  {currentListing.id ? (
                    <ActionBarMaybe
                      className={css.actionBarForProductLayout}
                      isOwnListing={isOwnListing}
                      listing={currentListing}
                      locale={locale}
                      editParams={{
                        id: listingId.uuid,
                        slug: listingSlug,
                        type: listingPathParamType,
                        tab: listingTab,
                      }}
                    />
                  ) : null}
                  <SectionGallery
                    listing={currentListing}
                    variantPrefix={config.layout.listingImage.variantPrefix}
                  />

                  {isMobile ? (
                    <Column lg={4} md={4} sm={12} className={css.mobileColumn}>
                      <div className={css.orderColumnForProductLayout}>
                        <BookingFormErrorBoundary>
                          <OrderPanel
                            rootClassName={css.productOrderPanel}
                            listing={currentListing}
                            isOwnListing={isOwnListing}
                            onSubmit={handleOrderSubmit}
                            showAuthorDetails
                            breakdownTitle={
                              <FormattedMessage id="BookingDatesForm.priceBreakdownTitle" />
                            }
                            authorLink={
                              <NamedLink
                                className={css.authorNameLink}
                                name="ProfilePage"
                                params={{
                                  ...(authorStoreCitySlug ? { city: authorStoreCitySlug } : {}),
                                  slug: generateStoreSlug(storeBreadcrumbLabel),
                                  id: ensuredAuthor?.id?.uuid
                                }}
                              >
                                {storeName || authorDisplayName}
                              </NamedLink>
                            }
                            title={
                              <FormattedMessage
                                id="ListingPage.orderTitle"
                                values={{ title: richTitle }}
                              />
                            }
                            titleDesktop={
                              <H4 as="h2" className={css.orderPanelTitle}>
                                <FormattedMessage
                                  id="ListingPage.orderTitle"
                                  values={{ title: richTitle }}
                                />
                              </H4>
                            }
                            payoutDetailsWarning={payoutDetailsWarning}
                            author={ensuredAuthor}
                            onManageDisableScrolling={onManageDisableScrolling}
                            onContactUser={onContactUser}
                            monthlyTimeSlots={monthlyTimeSlots}
                            onFetchTimeSlots={onFetchTimeSlots}
                            onFetchTransactionLineItems={
                              onFetchTransactionLineItems
                            }
                            lineItems={lineItems}
                            fetchLineItemsInProgress={fetchLineItemsInProgress}
                            fetchLineItemsError={fetchLineItemsError}
                            validListingTypes={config.listing.listingTypes}
                            marketplaceCurrency={config.currency}
                            dayCountAvailableForBooking={
                              config.stripe.dayCountAvailableForBooking
                            }
                            marketplaceName={config.marketplaceName}
                            showCartButton={true}
                            currentUser={currentUser}
                            isCurrentUserListing={
                              currentAuthor?.id?.uuid === currentUser?.id?.uuid
                            }
                            autoFocusBooking={shouldAutoFocusBooking}
                            onAddToCart={addToCartHandler}
                            addToCartInProgress={addToCartInProgress}
                            addToCartError={addToCartError}
                            cartNotification={cartNotification}
                            routeConfiguration={routeConfiguration}
                            isVerified={isVerified}
                            reviews={reviews}
                            rating={rating}
                            totalListings={totalListings}
                            isMobile={isMobile}
                            isCartDrawerOpen={isCartDrawerOpen}
                          />
                        </BookingFormErrorBoundary>
                        {/* FIX 3 (manual QA): About Owner + Secure Booking used to render
                            here, right after the gallery/before the tabs. Moved into the
                            shouldRenderRestOfPage block below (after Location/Map) to match
                            the confirmed mobile order: Tabs -> Location/Map -> About Owner
                            -> Secure Booking -> Instructions -> Reviews. See that block for
                            the actual JSX (unchanged otherwise - same components, same
                            props, same variant="orderPanel" CTAs from RAS-2522). */}
                      </div>
                    </Column>
                  ) : null}

                  <div
                    ref={triggerRef}
                    style={{
                      height: '1px',
                      opacity: 0,
                      pointerEvents: 'none',
                      position: 'relative',
                    }}
                  />
                  {isMobile && !shouldRenderRestOfPage ? (
                    <div aria-hidden="true" style={{ height: '20vh' }} />
                  ) : null}

                  {shouldRenderRestOfPage && (
                    <>
                      <DescriptionRentalTermsTabs
                        description={sanitizeHtml(description)}
                        condition={publicData?.Condition_of_the_item}
                        rentalTerms={{
                          cashSecurityDeposit:
                            publicData?.security_deposit || '',
                          cancelationPolicy:
                            publicData?.cancelation_policy || '',
                          rentalAccessibility:
                            publicData?.access_to_rentals || [],
                          trustVerification:
                            publicData?.Trust_and_verification || [],
                          reasonForRenting:
                            publicData?.reason_for_listing || [],
                        }}
                        listingCurrency={defaultPrice?.currency}
                        currentUserCurrency={selectedCurrency}
                        exchangeRates={exchangeRates}
                        faqs={showFaqs ? faqs : []}
                        intl={intl}
                      />

                      {/* FIX 3 (manual QA): confirmed mobile order is Tabs -> Location/Map
                          -> About Owner -> Secure Booking -> Instructions -> Reviews.
                          Map - desktop renders this in the sidebar instead (RAS-2494:
                          above Secure Booking, matching Figma node 16754:146839's Right
                          column order). Mobile keeps it here in the main flow, now
                          directly after the tabs. */}
                      {isMobile && (
                        <div id="location">
                          <SectionMapMaybe
                            geolocation={geolocation}
                            publicData={publicData}
                            listingId={currentListing.id}
                            mapsConfig={config.maps}
                            title={title}
                            currentUserOrigin={detectedCurrentLocation?.origin}
                            city={city}
                            region={region}
                            country={country}
                            showAddressInMarketplace={showAddressInMarketplace}
                            listingDistance={listingDistance}
                            showInMiles={showInMiles}
                            storeHours={storeHours}
                            storeTimeZone={storeTimeZone}
                          />
                        </div>
                      )}

                      {/* About Owner - mobile card (RAS-2522 CTAs), moved here from
                          right after the gallery so it follows Location/Map. Same
                          component/props/variant as before - JSX position only. */}
                      {isMobile && (
                        <div className={css.aboutOwnerMobile}>
                          <AboutOwner
                            usersEmailVerified={usersEmailVerified}
                            profile={ensuredAuthor}
                            listingsCount={totalListings}
                            reviews={reviews}
                            rating={rating}
                            verified={isVerified}
                            intl={intl}
                            onClick={() =>
                              !isOwnListing
                                ? onContactUser(ensuredAuthor)
                                : null
                            }
                            messageButtonVariant="secondary-outline"
                            showMessageIcon={false}
                            ctaSize="md"
                            storeLink={
                              <NamedLink
                                className={css.aboutOwnerMobileCta}
                                name="ProfilePage"
                                params={{
                                  ...(authorStoreCitySlug ? { city: authorStoreCitySlug } : {}),
                                  slug: generateStoreSlug(storeBreadcrumbLabel),
                                  id: ensuredAuthor?.id?.uuid
                                }}
                              >
                                <FormattedMessage id="AboutOwner.viewAllRentals" />
                              </NamedLink>
                            }
                            variant="orderPanel"
                          />
                        </div>
                      )}

                      {/* Secure Booking / Trust badges - mobile, moved here from right
                          after the gallery so it follows About Owner. Same
                          component/props - JSX position only. */}
                      {isMobile && (
                        <div className={css.BookingSupport}>
                          <BookingSupport
                            variant="lg"
                            showPaymentLogos
                            titleClassName={css.bookingSupportMobileTitle}
                            descriptionClassName={css.bookingSupportMobileDescription}
                          />
                        </div>
                      )}

                      {/* About Owner/Store - relocated from the sticky sidebar (RAS-2495),
                          desktop only. Message Owner lives in the top action bar
                          (RAS-2484), so it's suppressed here to avoid a duplicate. */}
                      {!isMobile && ensuredAuthor?.id && (
                        <AboutOwner
                          usersEmailVerified={usersEmailVerified}
                          profile={ensuredAuthor}
                          listingsCount={totalListings}
                          reviews={reviews}
                          rating={rating}
                          verified={isVerified}
                          intl={intl}
                          variant="section"
                          showMessageButton={false}
                          storeLink={
                            <NamedLink
                              className={css.aboutOwnerViewAllRentals}
                              name="ProfilePage"
                              params={{
                                ...(authorStoreCitySlug ? { city: authorStoreCitySlug } : {}),
                                slug: generateStoreSlug(storeBreadcrumbLabel),
                                id: ensuredAuthor?.id?.uuid
                              }}
                            >
                              <FormattedMessage id="AboutOwner.viewAllRentals" />
                              <i className="ri-arrow-right-line"></i>
                            </NamedLink>
                          }
                          isOwnListing={isOwnListing}
                        />
                      )}

                      {/* Instructions */}
                      {showInstructions && (
                        <InstructionsToUse content={instructions} />
                      )}

                      <div id="reviews">
                        <StoreReviews
                          reviews={reviews}
                          reviewsByUsers={reviewsByUsers}
                          averageRating={rating}
                          totalReviews={reviews ? reviews.length : 0}
                        />
                      </div>

                      {showOnlineReviewSummary && (
                        <ReviewSummary
                          listingTitle={title}
                          reviewSummary={{
                            content: online_review_summary?.content,
                            rating: online_review_summary?.rating,
                          }}
                        />
                      )}
                    </>
                  )}
                </Column>
                {/* Was previously rendered unconditionally, hidden on mobile only via
                    .desktopColumn's own `display: none` at <=1023px - meaning it stayed
                    fully mounted (and interactive) underneath the mobile OrderPanel
                    column above. Both instances share the same FieldCheckbox ids
                    (id={"{fee.feeType}"}, see OptionalChargesField.js), so on mobile
                    viewports the DOM briefly - or permanently, depending on browser
                    duplicate-id resolution - had two colliding `<label for="...">`
                    associations, which is exactly why add-ons checkboxes were unreliable
                    on mobile but fine on desktop (single instance there, no collision).
                    Gating this column in JS too, the same way .mobileColumn's sibling
                    already is above (`{isMobile ? (...) : null}`), ensures only one
                    OrderPanel/BookingDatesForm/BookingTimeForm is ever actually mounted
                    at a time. */}
                {!isMobile ? (
                <Column
                  lg={4}
                  md={4}
                  sm={12}
                  className={css.desktopColumn}
                  data-testid="booking-panel"
                >
                  <ListingActionBar onContactUser={onContactUser} />
                  {exchangeRates && exchangeRates.length > 0 ? (
                    <BookingFormErrorBoundary>
                      <OrderPanel
                        className={css.productOrderPanel}
                        listing={currentListing}
                        isOwnListing={isOwnListing}
                        usersEmailVerified={usersEmailVerified}
                        onSubmit={handleOrderSubmit}
                        showAuthorDetails={false}
                        breakdownTitle={
                          <FormattedMessage id="BookingDatesForm.priceBreakdownTitle" />
                        }
                        authorLink={
                          <NamedLink
                            className={css.authorNameLink}
                            name="ProfilePage"
                            params={{
                              ...(authorStoreCitySlug ? { city: authorStoreCitySlug } : {}),
                              slug: generateStoreSlug(storeBreadcrumbLabel),
                              id: ensuredAuthor?.id?.uuid
                            }}
                          >
                            {storeName || authorDisplayName}
                          </NamedLink>
                        }
                        title={
                          <FormattedMessage
                            id="ListingPage.orderTitle"
                            values={{ title: richTitle }}
                          />
                        }
                        titleDesktop={
                          <H4 as="h2" className={css.orderPanelTitle}>
                            <FormattedMessage
                              id="ListingPage.orderTitle"
                              values={{ title: richTitle }}
                            />
                          </H4>
                        }
                        payoutDetailsWarning={payoutDetailsWarning}
                        author={ensuredAuthor}
                        onManageDisableScrolling={onManageDisableScrolling}
                        onContactUser={onContactUser}
                        monthlyTimeSlots={monthlyTimeSlots}
                        onFetchTimeSlots={onFetchTimeSlots}
                        onFetchTransactionLineItems={
                          onFetchTransactionLineItems
                        }
                        lineItems={lineItems}
                        fetchLineItemsInProgress={fetchLineItemsInProgress}
                        fetchLineItemsError={fetchLineItemsError}
                        validListingTypes={config.listing.listingTypes}
                        marketplaceCurrency={config.currency}
                        dayCountAvailableForBooking={
                          config.stripe.dayCountAvailableForBooking
                        }
                        marketplaceName={config.marketplaceName}
                        showCartButton={true}
                        currentUser={currentUser}
                        isCurrentUserListing={
                          currentAuthor?.id?.uuid === currentUser?.id?.uuid
                        }
                        autoFocusBooking={shouldAutoFocusBooking}
                        onAddToCart={addToCartHandler}
                        addToCartInProgress={addToCartInProgress}
                        addToCartError={addToCartError}
                        cartNotification={cartNotification}
                        routeConfiguration={routeConfiguration}
                        isVerified={isVerified}
                        reviews={reviews}
                        totalListings={totalListings}
                      />
                    </BookingFormErrorBoundary>
                  ) : (
                    <OrderPanelSkeleton />
                  )}
                  {/* Map + Secure Booking - desktop-only sidebar order per Figma node
                      16754:146839's Right column: booking panel -> Location -> Features.
                      showAuthorDetails is off above so BookingSupport isn't duplicated. */}
                  {!isMobile && (
                    <>
                      <div id="location" className={css.sidebarLocationGap}>
                        <SectionMapMaybe
                          geolocation={geolocation}
                          publicData={publicData}
                          listingId={currentListing.id}
                          mapsConfig={config.maps}
                          title={title}
                          currentUserOrigin={detectedCurrentLocation?.origin}
                          city={city}
                          region={region}
                          country={country}
                          showAddressInMarketplace={showAddressInMarketplace}
                          listingDistance={listingDistance}
                          showInMiles={showInMiles}
                          storeHours={storeHours}
                          storeTimeZone={storeTimeZone}
                        />
                      </div>
                      <div className={css.BookingSupport}>
                        <BookingSupport variant="lg" showPaymentLogos />
                      </div>
                    </>
                  )}
                </Column>
                ) : null}
              </Grid>
            </Column>
          </Grid>

          {shouldRenderRestOfPage && (
            <div id="listings">
              <AuthorListings
                intl={intl}
                authorListings={authorListings}
                authorListingsInProgress={authorListingsInProgress}
                authorListingsError={authorListingsError}
                author={ensuredAuthor}
                isVerified={isVerified}
                currentUserCurrency={selectedCurrency}
                exchangeRates={exchangeRates}
                history={history}
                routes={routeConfiguration}
                queryID={queryID}
                currentUserCountryCode={currentUserLocationInfo?.country}
                locale={locale}
                hasLocationAvailable={!!currentUserLocationInfo?.origin}
                categoryLabel={categoryLabel}
                storeName={storeName}
              />
            </div>
          )}
          <BlogListing
            blogs={blogs || []}
            fetchBlogsInProgress={fetchBlogsInProgress}
            fetchBlogsError={fetchBlogsError}
          />

          {showSuggestedUses && <SuggestedUses uses={suggestedUses} />}
          {showSuggestedUses && <WhereWorksBest uses={suggestedUses} />}
          {showEnvironmentalImpactSection && environmentalImpactData && (
            <EnvironmentalImpact
              data={{
                title: intl.formatMessage({
                  id: 'ListingPage.environmentalImpact.title',
                }),
                subtitle: intl.formatMessage({
                  id: 'ListingPage.environmentalImpact.subtitle',
                }),
                ...environmentalImpactData,
              }}
            />
          )}
        </Container>
      </LayoutSingleColumn>
    </Page>
  );
};

ListingPageComponent.defaultProps = {
  currentUser: null,
  inquiryModalOpenForListingId: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: null,
  sendInquiryError: null,
  lineItems: null,
  fetchLineItemsError: null,
};

ListingPageComponent.propTypes = {
  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,
  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([
      LISTING_PAGE_DRAFT_VARIANT,
      LISTING_PAGE_PENDING_APPROVAL_VARIANT,
    ]),
  }).isRequired,

  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  inquiryModalOpenForListingId: string,
  showListingError: propTypes.error,
  callSetInitialValues: func.isRequired,
  reviews: arrayOf(propTypes.review),
  fetchReviewsError: propTypes.error,
  monthlyTimeSlots: object,
  // monthlyTimeSlots could be something like:
  // monthlyTimeSlots: {
  //   '2019-11': {
  //     timeSlots: [],
  //     fetchTimeSlotsInProgress: false,
  //     fetchTimeSlotsError: null,
  //   }
  // }
  sendInquiryInProgress: bool.isRequired,
  sendInquiryError: propTypes.error,
  onSendInquiry: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }),
};

const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    reviewsByUsers,
    fetchReviewsError,
    monthlyTimeSlots,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    authorListingsInProgress,
    authorListingsError,
    authorListingsIds = [],
    replicateListingInProgress,
    replicateListingError,
    isVerified,
    totalListings,
    blogs,
    fetchBlogsInProgress,
    fetchBlogsError,
  } = state.ListingPage;

  const { usersEmailVerified } = state.TransactionPage;

  const {
    currentUser,
    currentUserCurrency,
    currentUserLocationInfo,
    detectedCurrentLocation,
  } = state.user;

  const {
    exchangeRates,
    fetchRatesError,
    fetchRatesInProgress,
  } = state.exchangeRate;
  const { addToCartInProgress, addToCartError, notification, items: cartItemsByAuthor } = state.cart;
  const {
    lineItems: cartPageLineItems,
    fetchLineItemsInProgress: cartPageFetchLineItemsInProgress,
    fetchLineItemsError: cartPageFetchLineItemsError,
  } = state.CartPage;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const authorListingsRefs = authorListingsIds.map(id => ({
    id,
    type: 'listing',
  }));
  const authorListings = getMarketplaceEntities(state, authorListingsRefs);

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    reviewsByUsers,
    fetchReviewsError,
    monthlyTimeSlots,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
    selectedCurrency: currentUserCurrency,
    exchangeRates,
    fetchRatesError,
    fetchRatesInProgress,
    authorListings,
    authorListingsInProgress,
    authorListingsError,
    addToCartInProgress,
    addToCartError,
    cartNotification: notification,
    cartItemsByAuthor,
    cartPageLineItems,
    cartPageFetchLineItemsInProgress,
    cartPageFetchLineItemsError,
    replicateListingError,
    replicateListingInProgress,
    isVerified,
    totalListings,
    blogs,
    fetchBlogsInProgress,
    fetchBlogsError,
    currentUserLocationInfo,
    detectedCurrentLocation,
    usersEmailVerified,
  };
};

const mapDispatchToProps = dispatch => ({
  dispatch,
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params =>
    dispatch(fetchTransactionLineItems(params)),
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone)),
  onAddToCart: (config, params, queryID) =>
    dispatch(addToCart(config, params, queryID)),
  onReplicateListing: listingId => dispatch(replicateListingThunk(listingId)),
  onShowListing: (listingId, config) => dispatch(showListing(listingId, config)),
  onFetchExchangeRates: () => dispatch(fetchExchangeRates()),
  onIsUsersEmailVerified: (transactionId, userId) =>
    dispatch(isUsersEmailVerified(transactionId, userId)),
  onFetchAuthorMoreListings: (authorId, currentListingId) =>
    dispatch(fetchAuthorMoreListings(authorId, currentListingId)),
  onFetchMonthlyTimeSlots: listing => dispatch(fetchMonthlyTimeSlots(listing)),
  onFetchCartLineItems: (authorId, fetchLineItemsData) =>
    dispatch(fetchCartTransactionLineItems(authorId, fetchLineItemsData)),
  onUpdateCartItem: params => dispatch(updateCart(params)),
  onUpdateCartItemWithResult: params => dispatch(updateCartAndReportResult(params)),
  onUpdateCartItemsBatch: (itemIds, bookingDates) =>
    dispatch(updateCartItemsBatch(itemIds, bookingDates)),
  onRemoveCartItem: itemId => dispatch(removeFromCart(itemId)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  withViewport,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedListingPage);

export default ListingPage;
